<template>
  <div class="export-file-wrapper">
    <div class="export-file-card">
      <h2 v-if="showPendingStatus">{{ titleTxt }}</h2>

      <AnimationItem class="download-animation" :class="[status]" v-if="status === 'success'" :key="status"
                     :animation="{ animationData: paymentSuccessAnimation.default, loop: false }" />
      <AnimationItem class="download-animation" :class="[status]" v-if="status === 'error' || status === 'gone' || status === 'not_found'"
                     :key="status" :animation="{ animationData: paymentErrorAnimation.default, loop: false }" />
      <AnimationItem class="download-animation" :class="[status]" v-if="status === 'unauthorized'" :key="status"
                     :animation="{ animationData: paymentErrorAnimation.default, loop: false }" />
      <AnimationItem class="download-animation" :class="[status]" v-if="status === 'pending' && showPendingStatus" 
                      :key="status" :animation="{ animationData: paymentPendingAnimation.default, loop: false }" />
      <AnimationItem class="download-animation" :class="[status]" v-if="status === 'processing'" :key="status"
                     :animation="{ animationData: paymentPendingAnimation.default, loop: false }" />
      <AnimationItem class="download-animation" :class="[status]" v-if="status === 'timeout'" :key="status"
                     :animation="{ animationData: paymentPendingAnimation.default, loop: false }" />

      <div v-if="status==='success'" class="gk-tip gk-tip--blue text-center">
        <p>
          {{ $gettext('Your download might be blocked by a pop-up warning in the top right of the URL bar. Please click on the pop-up warning to allow the download from getgekko.com to continue. (This warning can differ per browser)')
          }}</p>
      </div>
      <div v-if="status === 'unauthorized'" class="text-center">
        <p>{{ $gettext('It appears you are not signed in. Sign in to download this file.') }}</p>
      </div>
      <div v-if="status === 'gone'">
        <p>{{ $gettext("The file download has expired and is no longer available for download.") }}</p>
      </div>
      <div v-if="status === 'not_found'">
        <p>{{ $gettext("This file doesn't exist. It may have been removed from the system, or the download link is incorrect or malformed. Please check the URL or generate a new download file if needed.") }}</p>
      </div>
      <div v-if="status === 'processing'">
        <p>
          {{ $gettext("This file is still processing and not yet ready to be downloaded, check back later to download this file.")
          }}</p>
      </div>
      <div v-if="status === 'timeout'">
        <p>{{ $gettext('This file will become available to download after') }}</p>
        <p>{{ timeoutSeconds % 60 }} {{ $gettext('seconds') }} {{ Math.floor(timeoutSeconds / 60) }} {{ $gettext('minutes') }}</p>
      </div>

      <div v-if="!['not_found', 'pending'].includes(status) || (status === 'pending' && showPendingStatus)" class="retry-box pt-3">
        <div v-if="status !== 'unauthorized' && status !== 'gone'"></div>
        <span>{{ $gettext('Important! This download file will only be available for seven days from the time it is created. Then it will be expired and you can no longer use this file. To download your data after the expiration date, you must generate a new download file.')
          }}</span>
        <span>{{ $gettext('If your download doesn\'t start immediately, try to') }}</span>&nbsp;<span
        class="text-blue cursor-pointer btn-link" @click="attemptDownload">{{ $gettext('download manually') }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import * as paymentSuccessAnimation from '@/css/animations/payment-complete.json'
import * as paymentErrorAnimation from '@/css/animations/payment-error.json'
import * as paymentPendingAnimation from '@/css/animations/payment-pending.json'
import AnimationItem from '@/components/dashboard/ui/AnimationItem.vue'

import GekkoButton from '@/components/GekkoButton'

import TemporaryFileService from '@/store/services/temporaryfile.service.js'

export default {
  name: 'DownloadFile',
  components: {
    AnimationItem,
    GekkoButton,
  },
  data() {
    return {
      status: null,
      filetype: null,
      uuid: null,
      timeoutSeconds: null,
      paymentSuccessAnimation: paymentSuccessAnimation,
      paymentErrorAnimation: paymentErrorAnimation,
      paymentPendingAnimation: paymentPendingAnimation,
      showPendingStatus: false,
      requestedFileUrl: null,
    }
  },
  computed: {
    titleTxt() {
      switch (this.status) {
        case 'pending':
          return this.$gettext('Your download will begin shortly.')

        case 'success':
          return this.$gettext('Your download is ready!')

        case 'error':
          return this.$gettext('Oops! There was an error.')

        case 'unauthorized':
          return this.$gettext('Unauthorized.')

        case 'not_found':
          return this.$gettext("This file doesn't exist.")
          
        case 'gone':
          return this.$gettext('The file download has expired.')

        case 'processing':
          return this.$gettext('File is processing')

        case 'timeout':
          return this.$gettext('You must wait before downloading this file. Upgrade to premium to instantly download any file.')

        default:
          throw 'Unknown status'
      }
    },
  },
  created() {
    this.filetype = this.$route.params['filetype']
    this.uuid = this.$route.params['uuid']
    setTimeout(() => this.showPendingStatus = true, 300)
    this.attemptDownload()
  },
  methods: {
    attemptDownload() {
      this.status = 'pending'
      if (this.filetype === 'export-file') {
        TemporaryFileService.download(this.uuid).then((response) => {
          if (response.status === 202) {
            this.status = 'processing'
          } else {
            this.status = 'success'
            this.requestedFileUrl = response.data
            const a = document.createElement('a')
            a.target = '_blank'
            a.href = this.requestedFileUrl
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
            window.URL.revokeObjectURL(this.requestedFileUrl)
          }
        }).catch((error) => {
          const code = error.data ? error.data.error : null
          if (error.status === 404) {
            this.status = 'not_found'
          } else if (error.status === 410) {
            this.status = 'gone'
          } else if (error.status === 429) {
            this.status = 'timeout'
            this.timeoutSeconds = error.headers['retry-after']
          } else if (error.status === 401 || (error.status === 400 && code === 'invalid_grant')) {
            this.status = 'unauthorized'
            this.$router.push({
              name: 'Login',
              query: {
                redirect: this.$route.fullPath,
              },
            })
          } else {
            this.status = 'error'
          }
        }).finally(() => {
          const alertTitle = this.$gettext('Download')
          let alertMsg = this.$gettext('An error occurred while attempting to download the file.')
          let alertVariant = 'warning'

          if (this.status === 'error') {
            alertVariant = 'danger'
          } else if (this.status === 'not_found') {
            alertMsg = this.$gettext("This file doesn't exist.")
            alertVariant = 'danger'
          } else if (this.status === 'gone') {
            alertMsg = this.$gettext('The file download has expired and is no longer available for download.')
          }

          if (['error', 'gone', 'not_found'].includes(this.status)) {
            this.$store.commit('setAlert', {
              title: alertTitle,
              text: alertMsg,
              variant: alertVariant,
              duration: 4
            })
          }
        })
      }
    },
  },
}
</script>

<style lang="scss">
.export-file-card {
  .download-animation {
    width: 80px !important;

    &.pending {
      svg {
        padding: 0.5rem;
      }
    }
  }
}
</style>

<style lang="scss" scoped>
.export-file-wrapper {
  padding-top: 3rem;
  text-align: center;

  .export-file-card {
    background-color: white;
    border: 1px solid $colorBorderDash;
    border-radius: $radiusLg;
    padding: 2rem;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    width: 100%;
    max-width: 450px;

    .retry-box {
      font-size: 1rem;
    }
  }
}
</style>